/**
 * Phase 10 R4 — useTicketFinancialSummary
 *
 * Composes existing hooks (parts, R2 labor, R3 tech pay) into a single
 * ticket-level Gross Profit summary. No new queries. Manager-gated.
 *
 * Returns null while any required source is loading or when disabled, so the
 * card can simply self-hide.
 *
 * AUTHORITY (FF-A / FF-B / FF-E):
 *   • Labor + tech-pay column WRITES are gated by FF-A's
 *     `trg_enforce_labor_entitlement` on `ticket_jobs`
 *     (migration `20260501120000_ff_a_labor_entitlement_trigger.sql`).
 *   • parts_lines WRITES are gated by FF-B's
 *     `trg_enforce_parts_line_entitlement`
 *     (migration `20260501130000_ff_b_parts_entitlement_triggers.sql`).
 *   • parts_lines READS are additionally gated by FF-E's
 *     `ff_e_parts_lines_select_entitled` RESTRICTIVE policy
 *     (migration `20260502120000_ff_e_financial_read_authority.sql`).
 *   • The client `labor_engine_enabled` / `ui_ticket_financials` flags
 *     are UI-display gates only; sessionStorage overrides cannot
 *     bypass server-side entitlement.
 *   • ticket_jobs labor / tech_pay column READS remain row-level RLS
 *     (shop boundary + role) — column-masking is a deferred FF-E
 *     PRODUCT/structural item (would require view/RPC migration of
 *     useTicketLaborLines / useTicketTechPayLines / useDayLaborLines /
 *     useDayTechPayLines). Write-side gating (FF-A) is the primary
 *     defense; non-entitled shops cannot generate fresh labor data.
 */
import { useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useUserRole } from '@/hooks/useUserRole';
import { useTicketPartsLines } from '@/hooks/parts/usePartsLines';
import { useTicketLaborLines } from '@/hooks/labor/useTicketLaborLines';
import { useTicketTechPayLines } from '@/hooks/labor/useTicketTechPayLines';
import { useShopFinancialSettings } from '@/hooks/labor/useShopFinancialSettings';
import { canSeeFinancials } from '@/lib/labor/resolveFinancialVisibility';
import {
  computeTicketFinancialSummary,
  type TicketFinancialSummary,
} from '@/lib/labor/computeTicketFinancialSummary';

export interface UseTicketFinancialSummaryResult {
  summary: TicketFinancialSummary | null;
  isLoading: boolean;
  enabled: boolean;
}

export function useTicketFinancialSummary(
  ticketId: string | undefined,
): UseTicketFinancialSummaryResult {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const ticketFinancialsEnabled = useExposureFlag('ui_ticket_financials');
  const techPayFlag = useExposureFlag('ui_technician_pay');
  const { role } = useUserRole();
  const { settings } = useShopFinancialSettings();

  // R5: visibility now flows through the shared resolver — OWNER/MANAGER
  // always; SERVICE_WRITER per shop's `financial_visibility_mode`;
  // TECHNICIAN never. Editing settings is governed by a separate gate.
  const allowed = canSeeFinancials(role, settings.financial_visibility_mode);
  const enabled =
    !!ticketId && !!shopId && laborEngineEnabled && ticketFinancialsEnabled && allowed;

  const { data: partsLines, isLoading: partsLoading } = useTicketPartsLines(
    enabled ? shopId : undefined,
    enabled ? ticketId : undefined,
  );
  const { data: laborData, isLoading: laborLoading } = useTicketLaborLines(
    ticketId,
    enabled,
  );
  // Tech pay rows only fetch when both flags are on; if ui_technician_pay is OFF
  // we deliberately leave techPayData undefined → labor cost surfaces as
  // 'insufficient' (honest) rather than fabricated.
  const techPayQueryEnabled = enabled && techPayFlag;
  const { data: techPayData, isLoading: techPayLoading } = useTicketTechPayLines(
    ticketId,
    techPayQueryEnabled,
  );

  const isLoading =
    enabled &&
    (partsLoading || laborLoading || (techPayQueryEnabled && techPayLoading));

  const summary = useMemo<TicketFinancialSummary | null>(() => {
    if (!enabled) return null;
    if (isLoading) return null;
    return computeTicketFinancialSummary({
      partsLines: partsLines ?? [],
      laborTotals: laborData?.totals ?? null,
      techPayLines: techPayData?.lines ?? null,
      techPayTotals: techPayData?.totals ?? null,
      // Use the same FALLBACK matrix path that useTicketPartsRollup uses, so
      // parts sell numbers stay consistent with PartsLaborRollup above.
      matrix: null,
    });
  }, [
    enabled,
    isLoading,
    partsLines,
    laborData?.totals,
    techPayData?.lines,
    techPayData?.totals,
  ]);

  return { summary, isLoading: !!isLoading, enabled };
}
